import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";

export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function guessMime(fileName = "", fileType = "") {
  const type = String(fileType || "").toLowerCase();
  if (type && type !== "application/octet-stream") return type;
  const name = String(fileName || "").toLowerCase();
  if (name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".png")) return "image/png";
  if (/\.jpe?g$/.test(name)) return "image/jpeg";
  if (name.endsWith(".gif")) return "image/gif";
  if (name.endsWith(".webp")) return "image/webp";
  return type || "application/octet-stream";
}

export function canPreviewFile(mime, fileName = "") {
  const type = String(mime || "").toLowerCase();
  const name = String(fileName || "").toLowerCase();
  if (type.includes("heic") || type.includes("heif") || /\.(heic|heif)$/.test(name)) return false;
  return type.startsWith("image/") || type === "application/pdf" || /\.(png|jpe?g|gif|webp|pdf)$/.test(name);
}

/**
 * Apre un file protetto (scaricato con il token Family): anteprima in dialog per immagini/PDF,
 * altrimenti download (o `window.FamilyApp.saveFile` nell'app mobile). Usato da Documenti e Ricette.
 */
export function useFamilyFileOpener(api) {
  const [viewer, setViewer] = useState(null);
  const close = useCallback(() => setViewer(null), []);
  const open = useCallback(async (path, fileName, fileType) => {
    try {
      const response = await api.get(path, { responseType: "blob" });
      const mime = guessMime(fileName, response.data?.type || fileType);
      const blob = mime && response.data?.type !== mime ? new Blob([response.data], { type: mime }) : response.data;
      if (canPreviewFile(mime, fileName)) {
        setViewer({ url: URL.createObjectURL(blob), name: fileName, type: mime });
        return;
      }
      if (typeof window.FamilyApp?.saveFile === "function") {
        window.FamilyApp.saveFile(fileName || "documento-family", await blobToDataUrl(blob));
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "documento";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error("Non è stato possibile aprire il documento");
    }
  }, [api]);
  return { viewer, open, close };
}

export function FamilyFileViewer({ viewer, onClose }) {
  useEffect(() => {
    if (!viewer) return undefined;
    const onKey = (event) => { if (event.key === "Escape") onClose(); };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      URL.revokeObjectURL(viewer.url);
    };
  }, [viewer, onClose]);
  if (!viewer) return null;
  const save = async (event) => {
    event.stopPropagation();
    if (!viewer?.url || typeof window.FamilyApp?.saveFile !== "function") return;
    try {
      const blob = await fetch(viewer.url).then((res) => res.blob());
      window.FamilyApp.saveFile(viewer.name || "documento-family", await blobToDataUrl(blob));
    } catch {
      toast.error("Non è stato possibile salvare il documento");
    }
  };
  const isImage = (viewer.type || "").startsWith("image/") || /\.(png|jpe?g|gif|webp)$/i.test(viewer.name || "");
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4" role="dialog" aria-modal="true" aria-label={viewer.name || "Documento"} onClick={onClose}>
      <button type="button" onClick={onClose} className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white" aria-label="Chiudi">
        <X className="h-5 w-5" />
      </button>
      {typeof window.FamilyApp?.saveFile === "function" ? (
        <button type="button" onClick={save} className="absolute right-16 top-4 z-10 rounded-full bg-white/15 px-3 py-2 text-xs font-bold text-white">
          Salva
        </button>
      ) : null}
      <p className="absolute bottom-5 left-1/2 z-10 max-w-[80%] -translate-x-1/2 truncate rounded-full bg-black/50 px-3 py-1 text-xs font-semibold text-white">{viewer.name}</p>
      {isImage ? (
        <img src={viewer.url} alt={viewer.name} className="max-h-[88dvh] max-w-full rounded-xl object-contain" onClick={(event) => event.stopPropagation()} />
      ) : (
        <iframe title={viewer.name} src={viewer.url} className="h-[88dvh] w-full max-w-3xl rounded-xl bg-white" onClick={(event) => event.stopPropagation()} />
      )}
    </div>
  );
}
